import {toBusinessUdemy} from '@udemy/organization';
import {UDData, useUDData} from '@udemy/ud-data';

export const UB_REF_QUERY_PARAM_VALUE = 'plan_comparison_matrix';

const RESTRICTED_COUNTRIES = ['IN', 'JP'];
const DEFAULT_TEAM_PLAN_COURSE_COUNT = 28000;
const RESTRICTED_TEAM_PLAN_COURSE_COUNT = 13000;

export const getComparisonMatrixHeaderData = (udData: UDData) => {
    return {
        teamPlan: {
            ctaUrl: toBusinessUdemy('sign-up', {ref: UB_REF_QUERY_PARAM_VALUE}, false, udData),
        },
        enterprisePlan: {
            ctaUrl: toBusinessUdemy('request-demo', {ref: UB_REF_QUERY_PARAM_VALUE}, false, udData),
        },
    };
};

export const useComparisonMatrixData = (
    gettext: (val: string) => string,
    _formatNumber: (val: number) => string,
    _getOrgNumericSiteStat: (val: string) => number,
    isValuePlanExperimentEnabled = false,
    starterPlanCourseCount: number,
    personalPlanCourseCount: number,
    isPersonalPlanLoading = true,
) => {
    const {Config: config} = useUDData();
    const priceCountry = config.price_country?.id ?? '';
    const teamPlanCourseCount = RESTRICTED_COUNTRIES.includes(priceCountry)
        ? RESTRICTED_TEAM_PLAN_COURSE_COUNT
        : DEFAULT_TEAM_PLAN_COURSE_COUNT;

    const _numberOfCourses = _formatNumber(personalPlanCourseCount);

    // Data for when Value Plan experiment is enabled (Value Plan + Personal Plan only)
    const valuePlanExperimentData = [
        {
            title: gettext('Learn new skills'),
            features: [
                {
                    title: gettext('Skills Categories'),
                    plans: [
                        {
                            value: gettext(
                                'Business, Finance & Accounting, Office Productivity, Personal Development, Design, Marketing, Lifestyle, Photography & Video, Health & Fitness, Music, Teaching & Academics',
                            ),
                            contentType: 'text',
                            key: 'value-plan',
                            isLoading: false,
                        },
                        {
                            value: gettext(
                                'Everything in Starter Plan PLUS AI, Web Development, Data Science, IT & Software, Certification Preparation',
                            ),
                            contentType: 'text',
                            key: 'personal-plan',
                            isLoading: false,
                        },
                    ],
                },
                {
                    title: gettext('Number of courses'),
                    plans: [
                        {
                            value: `${_formatNumber(starterPlanCourseCount)}+`,
                            contentType: 'text',
                            key: 'value-plan',
                            isLoading: false,
                        },
                        {
                            value: `${_formatNumber(personalPlanCourseCount)}+`,
                            contentType: 'text',
                            key: 'personal-plan',
                            isLoading: false,
                        },
                    ],
                },
                {
                    title: gettext('Quizzes'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'value-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Assignments'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'value-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                    ],
                },
            ],
        },
        {
            title: gettext('Get certified'),
            features: [
                {
                    title: gettext('Certificate of course completion'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'value-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Certification prep courses'),
                    plans: [
                        {
                            contentType: 'text',
                            key: 'value-plan',
                            value: '—',
                            isLoading: false,
                        },
                        {
                            value: gettext(
                                'Industry-leading certification courses from over 30 issuers including AWS, Microsoft, Google, PMI, and CompTIA',
                            ),
                            contentType: 'text',
                            key: 'personal-plan',
                            isLoading: false,
                        },
                    ],
                },
                {
                    title: gettext('Certification practice tests'),
                    plans: [
                        {
                            contentType: 'text',
                            key: 'value-plan',
                            value: '—',
                            isLoading: false,
                        },
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                    ],
                },
            ],
        },
        {
            title: gettext('Learn faster with guidance and practice'),
            features: [
                {
                    title: gettext('AI Assistant'),
                    tooltip: gettext(
                        'Get real-time answers to your questions on course content, break down complex answers into simple explanations, and generate quizzes to test your knowledge.',
                    ),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'value-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('AI Role Play'),
                    tooltip: gettext(
                        'Use AI simulations to practice your skills with real-time feedback. Rehearse scenarios like performance reviews, interviews, sales calls, and more.',
                    ),
                    plans: [
                        {
                            contentType: 'text',
                            key: 'value-plan',
                            value: '—',
                            isLoading: false,
                        },
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Coding exercises'),
                    plans: [
                        {
                            contentType: 'text',
                            key: 'value-plan',
                            value: '—',
                            isLoading: false,
                        },
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Technical labs'),
                    plans: [
                        {
                            contentType: 'text',
                            key: 'value-plan',
                            value: '—',
                            isLoading: false,
                        },
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                    ],
                },
            ],
        },
        {
            title: gettext('Learn without interruptions'),
            features: [
                {
                    title: gettext('Ad-free'),
                    tooltip: gettext(
                        'Enjoy uninterrupted learning to keep your focus on learning new skills.',
                    ),
                    plans: [
                        {
                            contentType: 'text',
                            key: 'value-plan',
                            value: '—',
                            isLoading: false,
                        },
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                    ],
                },
            ],
        },
    ];

    // Original data structure for when Value Plan experiment is disabled (Personal + Team + Enterprise)
    const originalData = [
        {
            title: gettext('Learner experience'),
            features: [
                {
                    title: gettext('Top-rated courses'),
                    tooltip: gettext(
                        'Our marketplace allows us to monitor popular topics and surface emerging skills while learner ratings help us identify the most effective courses.',
                    ),
                    plans: [
                        {
                            value: `${_numberOfCourses}+`,
                            contentType: 'text',
                            key: 'personal-plan',
                            isLoading: isPersonalPlanLoading,
                        },
                        {
                            value: `${teamPlanCourseCount}+`,
                            contentType: 'text',
                            key: 'team-plan',
                            isLoading: false,
                        },
                        {
                            note: gettext('(including international collection)'),
                            value: `${_getOrgNumericSiteStat('num_courses_rounded')}+`,
                            noteTooltip: gettext(
                                'Our international collection features original-language content in English, Portuguese, French, Italian, Indonesian, Japanese, Korean, Polish, Russian, Turkish, Arabic, Hindi, Vietnamese, and simplified and traditional Chinese.',
                            ),
                            contentType: 'text',
                            key: 'enterprise-plan',
                            isLoading: false,
                        },
                    ],
                },
                {
                    title: gettext('Certification prep courses and practice tests'),
                    tooltip: gettext(
                        'Prep employees for over 200 industry-recognized certification exams from issuers like AWS, Project Management Institute, Google, Microsoft, and more.',
                    ),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'team-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('AI-powered coding exercises'),
                    tooltip: gettext(
                        'Our instructors use AI to build Python, C++, Java, and Javascript coding exercises with solution code, test cases, and evaluation logic to provide learners with better exercises and comprehensive answers.',
                    ),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'team-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Goal-aligned recommendations'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'team-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Instructor Q&A'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'team-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Mobile app access'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'personal-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'team-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
            ],
        },
        {
            title: gettext('Admin experience'),
            features: [
                {
                    title: gettext('User adoption and engagement reports'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'team-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('24/7 customer support'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'team-plan',
                            value: true,
                        },
                        {
                            contentType: 'included',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Custom learning paths, courses, and user groups'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('User activity, learning trends, and benchmark insights'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Integration capabilities'),
                    plans: [
                        {
                            contentType: 'included',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
            ],
        },
        {
            title: gettext('Udemy Pro add-on for tech learners'),
            features: [
                {
                    title: gettext('Skills assessments'),
                    plans: [
                        {
                            contentType: 'add-on',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Labs and technical workspaces'),
                    plans: [
                        {
                            contentType: 'add-on',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Pre-built paths'),
                    plans: [
                        {
                            contentType: 'add-on',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
                {
                    title: gettext('Advanced insights'),
                    plans: [
                        {
                            contentType: 'add-on',
                            key: 'enterprise-plan',
                            value: true,
                        },
                    ],
                },
            ],
        },
    ];

    return {
        isLoading: isPersonalPlanLoading,
        data: isValuePlanExperimentEnabled ? valuePlanExperimentData : originalData,
    };
};
